import React, { useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { FiFilter, FiRotateCcw } from "react-icons/fi";
import {
  Actions,
  Body,
  Chip,
  Chips,
  CompactBody,
  CompactDropdown,
  CompactHeader,
  CompactPanel,
  Header,
  Meta,
  Panel,
  ResetButton,
  Title,
  TitleBlock,
  ToggleButton,
} from "./styles";

const TRANSITION = {
  duration: 0.28,
  ease: [0.16, 1, 0.3, 1],
};

export default function FilterPanel({
  title = "Filtros",
  meta,
  open,
  onToggle,
  onReset,
  onClose,
  chips = [],
  children,
  compact = false,
  fluxo = false,
  // Rótulo do botão-gatilho no modo compacto (default "Filtros"). Permite
  // um gatilho nomeado por domínio, ex.: "Marceneiro" em Entregas.
  triggerLabel = "Filtros",
}) {
  const compactRef = useRef(null);

  useEffect(() => {
    if (!compact || !open) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!compactRef.current || compactRef.current.contains(event.target)) {
        return;
      }

      if (onClose) {
        onClose();
        return;
      }

      if (open) {
        onToggle?.();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [compact, onClose, onToggle, open]);

  if (compact) {
    return (
      <CompactPanel ref={compactRef}>
        <Actions>
          <ToggleButton type="button" onClick={onToggle} $active={open} $fluxo={fluxo}>
            <FiFilter size={15} />
            {chips.length > 0 ? `${triggerLabel} (${chips.length})` : triggerLabel}
          </ToggleButton>
          {chips.length > 0 && (
            <ResetButton type="button" onClick={onReset} $fluxo={fluxo}>
              <FiRotateCcw size={15} />
              Limpar
            </ResetButton>
          )}
        </Actions>

        <AnimatePresence initial={false}>
          {open && (
            <CompactDropdown
              $fluxo={fluxo}
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.18 }}
            >
              <CompactHeader $fluxo={fluxo}>
                <TitleBlock>
                  <Title $fluxo={fluxo}>{title}</Title>
                  {meta && <Meta>{meta}</Meta>}
                </TitleBlock>
                <ResetButton type="button" onClick={onReset} $fluxo={fluxo}>
                  <FiRotateCcw size={15} />
                  Limpar
                </ResetButton>
              </CompactHeader>

              {chips.length > 0 && (
                <Chips>
                  {chips.map((chip) => (
                    <Chip key={chip}>{chip}</Chip>
                  ))}
                </Chips>
              )}

              <CompactBody>{children}</CompactBody>
            </CompactDropdown>
          )}
        </AnimatePresence>
      </CompactPanel>
    );
  }

  return (
    <Panel>
      <Header>
        <TitleBlock>
          <Title>{title}</Title>
          {meta && <Meta>{meta}</Meta>}
        </TitleBlock>

        <Actions>
          <ToggleButton type="button" onClick={onToggle} $active={open}>
            <FiFilter size={15} />
            {open ? "Ocultar filtros" : "Mostrar filtros"}
          </ToggleButton>
          <ResetButton type="button" onClick={onReset}>
            <FiRotateCcw size={15} />
            Limpar filtros
          </ResetButton>
        </Actions>
      </Header>

      {chips.length > 0 && (
        <Chips>
          {chips.map((chip) => (
            <Chip key={chip}>{chip}</Chip>
          ))}
        </Chips>
      )}

      <AnimatePresence initial={false}>
        {open && (
          <Body
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={TRANSITION}
          >
            {children}
          </Body>
        )}
      </AnimatePresence>
    </Panel>
  );
}
